import { useMemo } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { formatInTimeZone } from "date-fns-tz";

import Table from "@/components/table/table";
import { TimeSeriesConfig } from "@/components/graph/graph.types";
import { useSelectedTimeRange } from "@/features/dashboard/store";
import { ParameterTimeSeriesChartResult } from "@/lib/api";
import { useWidgetTimezone } from "@/features/shared/widget-context/widget-runtime-context";

import { renderValue } from "./render-value";

interface TimeseriesTableProps {
  data: ParameterTimeSeriesChartResult[];
  /** CMS-configured labels; when provided, used for column headers instead of parameter metadata */
  timeSeriesConfigs?: TimeSeriesConfig[];
}

const columnHelper = createColumnHelper<{
  timestamp: string;
  [columnId: string]: string;
}>();

const TimeseriesTable = ({ data, timeSeriesConfigs }: TimeseriesTableProps) => {
  const selectedTimeRange = useSelectedTimeRange();

  const parameterIdToLabel = useMemo(() => {
    const map = new Map<string, string>();
    if (!timeSeriesConfigs) return map;
    for (const config of timeSeriesConfigs) {
      for (const s of config.series) {
        if (s.label) map.set(s.parameter_id, s.label);
      }
    }
    return map;
  }, [timeSeriesConfigs]);

  const tz = useWidgetTimezone();
  const timezone = tz?.name === "UTC" ? tz.offset : (tz?.name ?? "");

  const tableData = useMemo(() => {
    const rows: Array<{
      timestamp: string;
      [columnId: string]: string;
    }> = [];

    data.forEach((result) => {
      if (!result.data) return;

      const datapoints = result.data.datapoints.filter((datapoint) => {
        const timestamp = new Date(datapoint.timestamp);
        return (
          timestamp < selectedTimeRange.timeEnd && timestamp > selectedTimeRange.timeStart
        );
      });
      datapoints.forEach((datapoint) => {
        const inData = rows.find((row) => row.timestamp === datapoint.timestamp);
        if (inData) {
          inData[result.parameter.id] = datapoint.value;
        } else {
          rows.push({
            timestamp: datapoint.timestamp,
            [result.parameter.id]: datapoint.value,
          });
        }
      });
    });

    return rows;
  }, [data, selectedTimeRange]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("timestamp", {
        header: "Timestamp",
        cell: (cell) => {
          const timestamp = cell.getValue();
          return formatInTimeZone(timestamp, timezone, "yyyy-MM-dd HH:mm");
        },
      }),
      ...data.map((result) => {
        const displayLabel =
          parameterIdToLabel.get(result.parameter.id) ?? result.parameter.label;
        return columnHelper.accessor(result.parameter.id, {
          header: displayLabel,
          cell: (cell) => renderValue(cell.getValue(), result.parameter.unit),
        });
      }),
    ],
    [data, parameterIdToLabel],
  );

  const tableState = useMemo(
    () => ({
      sorting: [
        {
          id: "timestamp",
          desc: false,
        },
      ],
    }),
    [],
  );

  const table = useReactTable({
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    data: tableData,
    columns,
    state: tableState,
  });

  return <Table table={table} />;
};

export default TimeseriesTable;
